/**
 * ChordWizard Component
 * AI-powered chord progression generator inspired by Unison Chord Genie
 * 
 * Features:
 * - Key and scale selection
 * - Genre-based presets
 * - One-click chord generation
 * - Piano roll preview
 * - Drag to track capability
 * - Playback preview
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wand2, 
  Music, 
  Music2,
  Play, 
  Pause, 
  RefreshCw, 
  Download, 
  Sparkles,
  ChevronDown,
  X,
  Volume2,
  Shuffle,
  Copy,
  Check,
  Layers,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import {
  NOTES,
  SCALES,
  GENRE_PROGRESSIONS,
  generateChordProgression,
  generateVariation,
  progressionToMidiEvents,
} from './ChordGenerator';

// Piano key component for mini preview
const PianoKey = ({ note, isBlack, isActive, isInChord }) => (
  <div
    className={cn(
      "relative transition-all duration-100",
      isBlack 
        ? "w-3 h-10 -mx-1.5 z-10 rounded-b" 
        : "w-5 h-16 rounded-b",
      isBlack
        ? isActive || isInChord
          ? "bg-indigo-600 shadow-lg shadow-indigo-500/50"
          : "bg-zinc-800 hover:bg-zinc-700"
        : isActive || isInChord
          ? "bg-indigo-400 shadow-lg shadow-indigo-400/50"
          : "bg-zinc-100 hover:bg-zinc-200 border-x border-b border-zinc-300"
    )}
  />
);

// Mini piano roll for chord preview
const ChordPianoRoll = ({ notes = [], activeNotes = [], className }) => {
  // Show 2 octaves (C3-B4 = 24 keys)
  const startNote = 48; // C3
  const keyCount = 25;
  
  const keys = [];
  for (let i = 0; i < keyCount; i++) {
    const midiNote = startNote + i;
    const noteInOctave = midiNote % 12;
    const isBlack = [1, 3, 6, 8, 10].includes(noteInOctave);
    const isInChord = notes.includes(midiNote);
    const isActive = activeNotes.includes(midiNote);
    
    keys.push(
      <PianoKey
        key={midiNote}
        note={midiNote}
        isBlack={isBlack}
        isActive={isActive}
        isInChord={isInChord}
      />
    );
  }
  
  return (
    <div className={cn("flex items-end justify-center", className)}>
      {keys}
    </div>
  );
};

// Chord card component
const ChordCard = ({ chord, index, isPlaying, onClick }) => (
  <motion.div
    className={cn(
      "flex flex-col items-center p-3 rounded-lg border cursor-pointer transition-all",
      isPlaying
        ? "bg-indigo-500/20 border-indigo-500 ring-2 ring-indigo-500/30"
        : "bg-zinc-800/50 border-zinc-700 hover:border-zinc-600"
    )}
    onClick={onClick}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
  >
    <span className="text-[10px] text-zinc-500 mb-1">Bar {index + 1}</span>
    <span className="text-lg font-bold text-white">{chord.symbol}</span>
    <span className="text-[10px] text-indigo-400 mt-1">{chord.root} {chord.type}</span>
  </motion.div>
);

// Genre selector button
const GenreButton = ({ genre, config, isSelected, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      "px-3 py-1.5 rounded-full text-xs font-medium transition-all",
      isSelected
        ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/30"
        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300"
    )}
  >
    {config.name}
  </button>
);

// Main ChordWizard component
export const ChordWizard = ({
  isOpen,
  onClose,
  onInsertToTrack,
  onAddToChordTrack,
  audioContext,
  className,
}) => {
  // State
  const [selectedKey, setSelectedKey] = useState('C');
  const [selectedScale, setSelectedScale] = useState('major');
  const [selectedGenre, setSelectedGenre] = useState('pop');
  const [progressionLength, setProgressionLength] = useState(4);
  const [complexity, setComplexity] = useState('medium');
  const [progression, setProgression] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentChordIndex, setCurrentChordIndex] = useState(-1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Refs
  const playbackRef = useRef(null);
  const oscillatorsRef = useRef([]);
  const gainNodeRef = useRef(null);
  
  // Initialize audio nodes
  useEffect(() => {
    if (audioContext) {
      gainNodeRef.current = audioContext.createGain();
      gainNodeRef.current.gain.value = 0.3;
      gainNodeRef.current.connect(audioContext.destination);
    }
    
    return () => {
      stopPlayback();
    };
  }, [audioContext]);
  
  // Generate new progression
  const handleGenerate = useCallback(() => {
    setIsGenerating(true);
    stopPlayback();
    
    // Small delay for animation effect
    setTimeout(() => {
      const newProgression = generateChordProgression({
        key: selectedKey,
        scale: selectedScale,
        genre: selectedGenre,
        length: progressionLength,
        complexity,
      });
      
      setProgression(newProgression);
      setCurrentChordIndex(-1);
      setIsGenerating(false);
    }, 300);
  }, [selectedKey, selectedScale, selectedGenre, progressionLength, complexity]);
  
  // Generate variation
  const handleVariation = useCallback(() => {
    if (!progression) return;
    
    setIsGenerating(true);
    stopPlayback();
    
    setTimeout(() => {
      const varied = generateVariation(progression);
      setProgression(varied);
      setCurrentChordIndex(-1);
      setIsGenerating(false);
    }, 200);
  }, [progression]);
  
  // Play a single chord
  const playChord = useCallback((notes) => {
    if (!audioContext || !gainNodeRef.current) return;
    
    // Stop any current oscillators
    oscillatorsRef.current.forEach(osc => {
      try { osc.stop(); } catch (e) { /* ignore already stopped */ }
    });
    oscillatorsRef.current = [];
    
    // Create oscillators for each note
    notes.forEach(midiNote => {
      const frequency = 440 * Math.pow(2, (midiNote - 69) / 12);
      
      const osc = audioContext.createOscillator();
      const noteGain = audioContext.createGain();
      
      osc.type = 'sine';
      osc.frequency.value = frequency;
      
      // Add slight attack/release
      noteGain.gain.setValueAtTime(0, audioContext.currentTime);
      noteGain.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
      
      osc.connect(noteGain);
      noteGain.connect(gainNodeRef.current);
      
      osc.start();
      oscillatorsRef.current.push(osc);
    });
  }, [audioContext]);
  
  // Stop chord playback
  const stopChord = useCallback(() => {
    oscillatorsRef.current.forEach(osc => {
      try {
        osc.stop();
      } catch (e) { /* ignore already stopped */ }
    });
    oscillatorsRef.current = [];
  }, []);
  
  // Play entire progression
  const playProgression = useCallback(() => {
    if (!progression || !audioContext) return;
    
    setIsPlaying(true);
    const bpm = progression.tempo;
    const msPerBeat = (60 / bpm) * 1000;
    const msPerBar = msPerBeat * 4;
    
    let chordIndex = 0;
    
    const playNextChord = () => {
      if (chordIndex >= progression.chords.length) {
        setIsPlaying(false);
        setCurrentChordIndex(-1);
        stopChord();
        return;
      }
      
      const chord = progression.chords[chordIndex];
      setCurrentChordIndex(chordIndex);
      playChord(chord.notes);
      
      chordIndex++;
      playbackRef.current = setTimeout(playNextChord, msPerBar);
    };
    
    playNextChord();
  }, [progression, audioContext, playChord, stopChord]);
  
  // Stop playback
  const stopPlayback = useCallback(() => {
    if (playbackRef.current) {
      clearTimeout(playbackRef.current);
      playbackRef.current = null;
    }
    stopChord();
    setIsPlaying(false);
    setCurrentChordIndex(-1);
  }, [stopChord]);
  
  // Toggle playback
  const togglePlayback = useCallback(() => {
    if (isPlaying) {
      stopPlayback();
    } else {
      playProgression();
    }
  }, [isPlaying, stopPlayback, playProgression]);
  
  // Handle chord click (preview single chord)
  const handleChordClick = useCallback((chord, index) => {
    setCurrentChordIndex(index);
    playChord(chord.notes);
    
    // Stop after 1 second
    setTimeout(() => {
      stopChord();
      if (!isPlaying) {
        setCurrentChordIndex(-1);
      }
    }, 1000);
  }, [playChord, stopChord, isPlaying]);
  
  // Insert progression to track
  const handleInsertToTrack = useCallback(() => {
    if (!progression) return;
    
    // Convert to MIDI clip format
    const midiClip = {
      id: `chord-wizard-${Date.now()}`,
      name: `${progression.key}${progression.scale === 'minor' ? 'm' : ''} - ${GENRE_PROGRESSIONS[progression.genre].name}`,
      startTime: 0,
      duration: progression.chords.length * 4, // bars * 4 beats
      notes: [],
    };
    
    // Add notes
    progression.chords.forEach((chord, chordIndex) => {
      chord.notes.forEach(midiNote => {
        midiClip.notes.push({
          id: `note-${Date.now()}-${chordIndex}-${midiNote}`,
          pitch: midiNote,
          startTime: chordIndex * 4, // in beats
          duration: 3.9, // slightly less than 4 beats
          velocity: 80,
        });
      });
    });
    
    onInsertToTrack?.(midiClip, progression);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [progression, onInsertToTrack]);
  
  // Add progression to Chord Track
  const handleAddToChordTrack = useCallback(() => {
    if (!progression || !onAddToChordTrack) return;
    
    // Convert progression to chord regions for Chord Track
    const bpm = progression.tempo;
    const secondsPerBeat = 60 / bpm;
    const secondsPerBar = secondsPerBeat * 4;
    
    const chordRegions = progression.chords.map((chord, index) => ({
      id: `chord-wizard-region-${Date.now()}-${index}`,
      startTime: index * secondsPerBar,
      duration: secondsPerBar,
      root: chord.root,
      type: chord.type,
      symbol: chord.symbol,
      notes: chord.notes,
      colorIndex: index % 8,
    }));
    
    onAddToChordTrack(chordRegions, progression);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [progression, onAddToChordTrack]);
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && onClose?.()}
      >
        <motion.div
          className={cn(
            "w-full max-w-2xl bg-zinc-900 rounded-2xl border border-zinc-700 shadow-2xl overflow-hidden",
            className
          )}
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          data-testid="chord-wizard-modal"
        >
          {/* Header */}
          <div className="relative px-6 py-4 border-b border-zinc-800 bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Chord Wizard</h2>
                <p className="text-xs text-zinc-400">Generate pro-level chord progressions instantly</p>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
              data-testid="chord-wizard-close-btn"
            >
              <X className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
          
          {/* Controls */}
          <div className="p-6 space-y-6">
            {/* Key and Scale Selection */}
            <div className="grid grid-cols-2 gap-4">
              {/* Key Selector */}
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-2">Key</label>
                <div className="relative">
                  <select
                    value={selectedKey}
                    onChange={(e) => setSelectedKey(e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white appearance-none cursor-pointer hover:border-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                    data-testid="chord-wizard-key-select"
                  >
                    {NOTES.map(note => (
                      <option key={note} value={note}>{note}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                </div>
              </div>
              
              {/* Scale Selector */}
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-2">Scale</label>
                <div className="relative">
                  <select
                    value={selectedScale}
                    onChange={(e) => setSelectedScale(e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white appearance-none cursor-pointer hover:border-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                    data-testid="chord-wizard-scale-select"
                  >
                    {Object.keys(SCALES).map(scale => (
                      <option key={scale} value={scale}>
                        {scale.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                </div>
              </div>
            </div>
            
            {/* Genre Selection */}
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-2">Genre / Style</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(GENRE_PROGRESSIONS).map(([key, config]) => (
                  <GenreButton
                    key={key}
                    genre={key}
                    config={config}
                    isSelected={selectedGenre === key}
                    onClick={() => setSelectedGenre(key)}
                  />
                ))}
              </div>
            </div>
            
            {/* Length and Complexity */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-2">Length (Bars)</label>
                <div className="flex gap-2">
                  {[4, 8, 16].map(len => (
                    <button
                      key={len}
                      onClick={() => setProgressionLength(len)}
                      className={cn(
                        "flex-1 py-2 rounded-lg text-sm font-medium transition-all",
                        progressionLength === len
                          ? "bg-indigo-500 text-white"
                          : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                      )}
                    >
                      {len}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-2">Complexity</label>
                <div className="flex gap-2">
                  {['simple', 'medium', 'complex'].map(c => (
                    <button
                      key={c}
                      onClick={() => setComplexity(c)}
                      className={cn(
                        "flex-1 py-2 rounded-lg text-sm font-medium transition-all capitalize",
                        complexity === c
                          ? "bg-indigo-500 text-white"
                          : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                      )}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Generate Button */}
            <motion.button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold text-lg shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              data-testid="chord-wizard-generate-btn"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Chord Progression
                </>
              )}
            </motion.button>
            
            {/* Generated Progression Display */}
            <AnimatePresence>
              {progression && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4"
                >
                  {/* Progression Info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-zinc-400">
                        <span className="text-white font-medium">{progression.key}</span>
                        {' '}{progression.scale}
                      </span>
                      <span className="text-sm text-zinc-400">
                        <span className="text-indigo-400 font-mono">{progression.romanNumerals.join(' - ')}</span>
                      </span>
                      <span className="text-sm text-zinc-400">
                        <span className="text-white">{progression.tempo}</span> BPM
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleVariation}
                        className="w-8 h-8"
                        title="Generate Variation"
                      >
                        <Shuffle className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={togglePlayback}
                        className="w-8 h-8"
                        title={isPlaying ? "Stop" : "Play"}
                      >
                        {isPlaying ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Chord Cards */}
                  <div className="grid grid-cols-4 gap-2" data-testid="chord-wizard-progression">
                    {progression.chords.slice(0, 8).map((chord, index) => (
                      <ChordCard
                        key={`${chord.symbol}-${index}`}
                        chord={chord}
                        index={index}
                        isPlaying={currentChordIndex === index}
                        onClick={() => handleChordClick(chord, index)}
                      />
                    ))}
                  </div>
                  
                  {/* Show more if longer progression */}
                  {progression.chords.length > 8 && (
                    <div className="grid grid-cols-4 gap-2">
                      {progression.chords.slice(8).map((chord, index) => (
                        <ChordCard
                          key={`${chord.symbol}-${index + 8}`}
                          chord={chord}
                          index={index + 8}
                          isPlaying={currentChordIndex === index + 8}
                          onClick={() => handleChordClick(chord, index + 8)}
                        />
                      ))}
                    </div>
                  )}
                  
                  {/* Piano Roll Preview */}
                  <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                    <div className="text-xs text-zinc-500 mb-2 text-center">Piano Roll Preview</div>
                    <ChordPianoRoll
                      notes={currentChordIndex >= 0 ? progression.chords[currentChordIndex]?.notes : []}
                      className="justify-center"
                    />
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={handleInsertToTrack}
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid="chord-wizard-insert-btn"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Added!
                        </>
                      ) : (
                        <>
                          <Layers className="w-4 h-4 mr-2" />
                          Insert MIDI
                        </>
                      )}
                    </Button>
                    
                    {onAddToChordTrack && (
                      <Button
                        onClick={handleAddToChordTrack}
                        className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white"
                        data-testid="chord-wizard-add-to-track-btn"
                      >
                        <Music2 className="w-4 h-4 mr-2" />
                        Add to Chord Track
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      onClick={handleGenerate}
                      className="border-zinc-600"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Regenerate
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ChordWizard;
